// @-agent mention helpers for the Simone composer. The composer is a native
// <textarea> (unlike the /chat app's contenteditable), so these are textarea-
// safe: caret = node.selectionStart, insert = a value splice. Kept small +
// dependency-free (shared can't import from the chat package).

// Detect an @-mention trigger in `value` at `caret`. Returns { query, start, end }
// or null. The `@` must be at start-of-value or preceded by whitespace, and only
// mention-ish chars may follow (letters/digits/._- and spaces for names).
import { agentDisplayName } from '../../functions/agentDisplayName.js'

export const detectAgentMention = (value, caret) => {
  if (caret == null || !value) return null
  const upto = value.slice(0, caret)
  const at = upto.lastIndexOf('@')
  if (at < 0) return null
  if (at > 0 && !/\s/.test(value[at - 1])) return null
  const after = upto.slice(at + 1)
  if (!/^[a-zA-Z0-9._\- ]*$/.test(after)) return null
  return { query: after, start: at, end: caret }
}

// Splice `insert` into `value` over [start, end). Returns { value, caret }.
export const spliceAt = (value, start, end, insert) => ({
  value: String(value).slice(0, start) + insert + String(value).slice(end),
  caret: start + insert.length
})

// The text to insert for a picked agent — `@handle ` (email local-part, else name).
export const agentInsertText = (agent) => {
  if (!agent) return ''
  const handle = (agent.email || '').split('@')[0] || agent.name
  return handle ? `@${handle} ` : ''
}

// Is a roster entry an addressable agent (has a userId to target)?
const isAgentEntry = (a) => !!(a && a.userId && (a.kind === 'agent' || a.isAgent))

// Merge the agent roster from _agents + people (either may hold agents depending
// on what's loaded), deduped by userId.
export const agentRoster = (root) => {
  const out = []
  const seen = new Set()
  for (const a of [...((root && root._agents) || []), ...((root && root.people) || [])]) {
    if (!isAgentEntry(a)) continue
    const key = String(a.userId)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(a)
  }
  return out
}

// Transform SDK-listed Mongo agents to the picker shape (mirrors
// team-and-agents _agentsFromMongo — userId is the _targetAgentId).
export const agentsFromMongo = (list) =>
  (Array.isArray(list) ? list : [])
    .map((u) => ({
      id: String(u.username || u.id || u._id || '').toLowerCase(),
      name: u.name || u.username || 'Agent',
      kind: 'agent',
      avatar: u.avatar || null,
      userId: u.id || u._id || null,
      email: u.email || null,
      // the record's per-language names (server-seeded agents), shown by
      // agentMentionLabel in the active interface language
      nameI18n: (u.metadata && u.metadata.nameI18n) || null
    }))
    .filter((a) => a.userId)

// The name the picker shows for a roster entry: its name in `lang` when the
// record carries one (core-wl's agentDisplayName), else the record name.
export const agentMentionLabel = (a, lang) =>
  agentDisplayName({ name: (a && a.name) || '', metadata: { nameI18n: a && a.nameI18n } }, lang)

// FALSE-ZERO-1 surface 4 — the roster-load OUTCOME shape, pulled out of
// Chat.js's `ensureAgentRoster` so the "always write, [] included" +
// "record failure, never swallow it" contract is unit-testable without a
// live SDK or a mounted element. `_agents` used to be written ONLY when
// the resolved list was non-empty and the catch path swallowed the error
// into a bare `root._agentsLoaded = false` reset — so `root._agents ===
// undefined` was ambiguous between "this workspace genuinely has zero
// agents" and "the roster load failed", and consumers rendered both as a
// confident "0". These two return the exact `root.update(...)` payload for
// each outcome; Chat.js's `ensureAgentRoster` mirrors this logic inline
// (kept in sync, same idiom as `agentsFromMongo`/`agentRoster` above).
// @testOnly — exported for mention.test.js; no live code imports or calls it.
export const agentRosterLoadedUpdate = (raw) => {
  const list = Array.isArray(raw) ? raw : (raw && raw.data) || []
  // Always present, [] included — a real, verified zero must still render
  // as zero, distinguishable from "never loaded" (null) and "failed"
  // (_agentsError set).
  return { _agents: agentsFromMongo(list), _agentsError: null }
}

// @testOnly — exported for mention.test.js; no live code imports or calls it.
export const agentRosterFailedUpdate = (err) => ({
  _agentsError: (err && err.message) || 'Failed to load agents'
})

// Filter the roster by a query (name, the name shown in `lang`, email
// local-part). Empty query → the
// whole roster. Capped for the dropdown.
export const filterAgentsForMention = (root, query, limit = 8, lang) => {
  const roster = agentRoster(root)
  const q = String(query || '')
    .trim()
    .toLowerCase()
  const matched = q
    ? roster.filter((a) => {
        const name = String(a.name || '').toLowerCase()
        const shown = String(agentMentionLabel(a, lang) || '').toLowerCase()
        const handle = String((a.email || '').split('@')[0] || '').toLowerCase()
        return name.includes(q) || shown.includes(q) || handle.includes(q)
      })
    : roster
  return matched.slice(0, limit)
}
